<template>
    <div class="appointment-timer-process-modal">
        <TimerModal
            v-if="hasAppointments"
            data-qa="appointment-timer-modal"
            :title="$t('funnel.edit.sideBar.flow.timerAppointment')"
            :timer-tip-link="$t('funnel.edit.appointment.timer.field.proTipUrl')"
            :timer-tip-text="$t('funnel.edit.timer.field.proTip')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #timerSummary>
                <!-- eslint-disable vue/no-v-html -->
                <span v-html="summaryText" />
                <!-- eslint-enable vue/no-v-html -->
            </template>

            <template #timerParameters>
                <div class="progress-slider">
                    <div v-if="validationErrorMessages !== null" class="errorMessage">
                        <DsInlineAlert leading-icon type="critical">
                            {{ validationErrorMessages }}
                        </DsInlineAlert>
                    </div>

                    <div class="appointment-timer-formLabel">
                        {{ $t('funnel.edit.timerAppointment.subtitle') }}
                    </div>

                    <div class="top-border-triple-wrapper">
                        <div class="formSpacer">
                            <DsInputField
                                v-model="waitDelayDays"
                                data-qa="appointment-timer-wait-delay-days"
                                type="number"
                                :step="1"
                                :min="waitDelayDaysRange.minWaitDelay"
                                :max="waitDelayDaysRange.maxWaitDelay"
                                :label="$t('funnel.edit.timer.waitDelayType.Day')"
                            />
                        </div>

                        <div class="formSpacer">
                            <DsInputField
                                v-model="waitDelayHours"
                                data-qa="appointment-timer-wait-delay-hours"
                                type="number"
                                :step="1"
                                :min="waitDelayHoursRange.minWaitDelay"
                                :max="waitDelayHoursRange.maxWaitDelay"
                                :label="$t('funnel.edit.timer.waitDelayType.Hour')"
                            />
                        </div>

                        <div class="formSpacer">
                            <DsInputField
                                v-model="waitDelayMinutes"
                                data-qa="appointment-timer-wait-delay-minutes"
                                type="number"
                                :step="1"
                                :min="waitDelayMinutesRange.minWaitDelay"
                                :max="waitDelayMinutesRange.maxWaitDelay"
                                :label="$t('funnel.edit.timer.waitDelayType.Minute')"
                            />
                        </div>
                    </div>

                    <div class="top-border-double-wrapper">
                        <div class="formSpacer">
                            <DsMultiselect
                                v-model="whenToRun"
                                class="contact-field-multiselect"
                                data-qa="appointment-timer-when-to-run"
                                bind-value-only
                                searchable
                                label-prop="label"
                                value-prop="id"
                                :options="whenToRunOptions"
                            />
                        </div>

                        <div class="formSpacer, appointment-timer-modifier-type-text">
                            {{ $t('funnel.edit.timerAppointment.appointment') }}
                        </div>
                    </div>
                </div>
            </template>
        </TimerModal>

        <DsModal
            data-qa="appointment-timer-modal-warning"
            :is-open="!hasAppointments"
            dialog
            size="xs"
            @close="closeModal"
        >
            <span class="appointment-timer-confirm-title">{{ $t('funnel.edit.appointment.timer.dialog.no.appointments.title') }}</span>

            <span>{{ $t('funnel.edit.appointment.timer.dialog.no.appointments.description') }}</span>

            <div class="appointment-timer-confirm-button">
                <DsTextButton @click="closeModal">
                    {{ $t('funnel.edit.appointment.timer.dialog.no.appointments.button') }}
                </DsTextButton>
            </div>
        </DsModal>
    </div>
</template>

<script>
import TimerModal from '../timerModal/TimerModal.vue';
import { DECISION_TYPE, GOALS } from '../../../../utils/editor.constants';
import { findIncomingCells } from '../../../../utils/cell/automation-edge-utils';

const APPOINTMENT_CONSTANTS = {
    MINUTE: 'Minute',
    HOUR: 'Hour',
    DAY: 'Day',
};

const APPOINTMENT_RANGES = {
    [APPOINTMENT_CONSTANTS.MINUTE]: [0, 9999],
    [APPOINTMENT_CONSTANTS.HOUR]: [0, 9999],
    [APPOINTMENT_CONSTANTS.DAY]: [0, 9999],
};

const APPOINTMENT_MODIFIER_CONSTANTS = { BEFORE: 'Before', AFTER: 'After' };

export default {
    APPOINTMENT_CONSTANTS,
    APPOINTMENT_MODIFIER_CONSTANTS,
    APPOINTMENT_RANGES,
    components: {
        TimerModal,
    },

    props: {
        cells: {
            type: Array,
            default: () => ([]),
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    errorMessages: null,
                    waitDelay: null,
                    waitDelayType: null,
                    waitDelayModifierType: null,
                    contactField: null,
                    yearType: null,
                    startHour: null,
                    startTime: null,
                    startMinute: null,
                    startAmPm: null,
                    useContactTimeZone: null,
                    timeOfDayType: null,
                },
            }),
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            waitDelayDays: this.cell?.value?.waitDelayDays === null ? 0 : this.cell.value.waitDelayDays,
            waitDelayHours: this.cell?.value?.waitDelayHours || 0,
            waitDelayMinutes: this.cell?.value?.waitDelayMinutes || 0,
            whenToRun: this.cell?.value?.whenToRun || 'Before',
            validationErrorMessages: null,
        };
    },

    computed: {

        hasAppointments() {
            const incomingCells = findIncomingCells(this.cell.meta.parent, this.cells)?.filter((element) => [GOALS.GOAL_APPOINTMENT_EVENT, DECISION_TYPE].includes(element.type));

            if (!incomingCells || incomingCells.length === 0) {
                return false;
            }

            const hasAppointmentEventCell = !!incomingCells?.find((element) => element.type === GOALS.GOAL_APPOINTMENT_EVENT);

            if (hasAppointmentEventCell) {
                return true;
            }

            const incomingDDCell = incomingCells?.find((element) => element.type === DECISION_TYPE);

            if (!incomingDDCell) {
                return false;
            }

            const { id: decisionCellId } = incomingDDCell;

            const decisionIncomingCells = findIncomingCells(decisionCellId, this.cells);

            const appointmentSource = decisionIncomingCells?.find((element) => element.type === GOALS.GOAL_APPOINTMENT_EVENT);

            return !!appointmentSource;
        },

        waitDelayDaysRange() {
            const range = APPOINTMENT_RANGES[APPOINTMENT_CONSTANTS.DAY];

            return { minWaitDelay: range[0], maxWaitDelay: range[1] };
        },

        waitDelayHoursRange() {
            const range = APPOINTMENT_RANGES[APPOINTMENT_CONSTANTS.HOUR];

            return { minWaitDelay: range[0], maxWaitDelay: range[1] };
        },

        waitDelayMinutesRange() {
            const range = APPOINTMENT_RANGES[APPOINTMENT_CONSTANTS.MINUTE];

            return { minWaitDelay: range[0], maxWaitDelay: range[1] };
        },


        whenToRunOptions() {
            return [
                { id: APPOINTMENT_MODIFIER_CONSTANTS.BEFORE, label: this.$t('funnel.edit.timerAppointment.before') },
                { id: APPOINTMENT_MODIFIER_CONSTANTS.AFTER, label: this.$t('funnel.edit.timerAppointment.after') },
            ];
        },

        summaryText() {
            let summary = `${this.$t('delay.timer.run')} <b>${this.waitDelayDays ? this.waitDelayDays : 0} ${this.$t('funnel.edit.timer.waitDelayType.Day')}`;

            summary += ` ${this.waitDelayHours ? this.waitDelayHours : 0} ${this.$t('funnel.edit.timer.waitDelayType.Hour')}`;
            summary += ` ${this.waitDelayMinutes ? this.waitDelayMinutes : 0} ${this.$t('funnel.edit.timer.waitDelayType.Minute')}`;
            summary += ` ${this.whenToRunOptions.find((type) => type.id === this.whenToRun)?.label}</b> ${this.$t('funnel.edit.timer.field.theContact')}`;
            summary += ` ${this.$t('funnel.edit.timerAppointment.appointment')}`;

            return summary;
        },

        summaryAsCellLabel() {
            return this.summaryText.replaceAll('<b>', '').replaceAll('</b>', '');
        },
    },

    methods: {
        closeModal() {
            this.$emit('close-modal');
        },

        updateConfig() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};
            cell.value.waitDelayDays = this.waitDelayDays ? parseInt(this.waitDelayDays, 10) : 0;
            cell.value.waitDelayHours = this.waitDelayHours ? parseInt(this.waitDelayHours, 10) : 0;
            cell.value.waitDelayMinutes = this.waitDelayMinutes ? parseInt(this.waitDelayMinutes, 10) : 0;
            cell.value.whenToRun = this.whenToRun;
            cell.value.name = this.summaryAsCellLabel.replace(/(.{1,25})(\s|$)/g, '$1\n');

            this.$emit('update-cell-value', { cell });
        },
    },
};
</script>

<style lang="scss" scoped>
.modal-body, .inf-section-body {
    padding: px-to-rem(0);
}

.appointment-timer-confirm-button {
    display: flex;
    justify-content: flex-end;
}
.appointment-timer-confirm-title {
    font-size: large;
    margin-bottom: px-to-rem(3);
    padding-bottom: px-to-rem(10);
}

.appointment-timer-formLabel {
    font-size: $font-size-md;
}

.formSpacer {
    padding: $spacing-025;
    display: inline-block;
    max-width: px-to-rem(500);
}

.appointment-timer-modifier-type-text {
    padding-bottom: px-to-rem(25);
}

#timerConfig label {
    vertical-align: baseline;
}

#timerConfig {
    height: px-to-rem(150);
    width: px-to-rem(300);
    white-space: nowrap;
    overflow-x: auto;
}

.top-border-triple-wrapper {
    display: grid;
    grid-template-columns: 20% 20% 20%;
    grid-gap: 1rem;
    align-items: center;
    padding-top: $spacing-125;
}

.top-border-double-wrapper {
    display: grid;
    grid-template-columns: 40% 40%;
    grid-gap: 1rem;
    align-items: center;
    padding-top: $spacing-125;
}

.modal-content-wrapper{
    min-width: px-to-rem(700);
}

.left-border {
    border-left: px-to-rem(1) solid $color-gray-400;
}

.right-border {
    border-right: px-to-rem(1) solid $color-gray-400;
}

.timer-option {
    vertical-align: top;
}

#timeOfDay_start .timer-option select,
#timeOfDay_stop .timer-option select{
    width: px-to-rem(55);
}

.timer-parameter {
    font-weight: 600;
}

.timer-time-zone {
    color: $color-blue;
    font-weight: 600;
}

#timer-time-zone-padded-box {
    padding-left: $spacing-125;
    padding-right: $spacing-125;
}

#timer-time-zone-details {
    padding-top: px-to-rem(2);
    white-space: nowrap;
}

#timer-time-zone-example-header {
    padding-top: $spacing-200;
}

.errorMessage {
    color: $color-red;
    padding-bottom: $spacing-050;
}

.small-date-picker {
    max-width: px-to-rem(125);
}

.time-day-type {
    margin-bottom: $spacing-100;
}

.timer-horizontal-radio {
    display: flex;
}

#timerConfig .infusion-calendar-wrapper {
    display: contents;
}

@media only screen and (max-width: px-to-rem(600)) {
    #timerConfig {
        display: block;
        width: 100%
    }
}
@media only screen and (min-width: px-to-rem(601)) {
    #timerConfig {
        min-width: px-to-rem(600);
    }
}
</style>
